// styles
import "@/firebaseConfig";
import "v-calendar/style.css";
import "vue-select/dist/vue-select.css";
import "@vuepic/vue-datepicker/dist/main.css";
import "./styles/base.scss";
import "primeicons/primeicons.css";

// vue / app
import { createApp, watch } from "vue";
import App from "@/App.vue";
import router from "@/router";
import store from "@/stores";

// PrimeVue
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";

// other UI libs
import VCalendar from "v-calendar";
import VueDatePicker from "@vuepic/vue-datepicker";
import VueSelect from "vue-select";
import { Tabs, Tab } from "vue3-tabs-component";
import { Toaster } from "vue-sonner";
import "vue-sonner/style.css";

// icons for vue-select
import OpenIndicator from "@/components/icons/IconOpenIndicator.vue";
import Deselect from "@/components/icons/IconDeselect.vue";

// i18n
import i18n, { initialLocale } from "@/i18n";

// Day.js
import dayjs from "dayjs";
import "dayjs/locale/da";
import "dayjs/locale/en";
import { setDayjsLocale } from "@/utils/dayjsLocale";

// ---- vue-select default icons
VueSelect.props.components.default = () => ({ OpenIndicator, Deselect });

// Start auth listener immediately; don't block app mount here
store.dispatch("auth/initAuthListenerOnce").catch(() => {});

// ---- click-outside directive
const clickOutside = {
  beforeMount: (el, binding) => {
    el.eventSetDrag = () => el.setAttribute("data-dragging", "yes");
    el.eventClearDrag = () => el.removeAttribute("data-dragging");
    el.eventOnClick = (event) => {
      const dragging = el.getAttribute("data-dragging");
      if (
        !(el === event.target || el.contains(event.target)) &&
        !dragging &&
        !event.target.classList.contains("ignoreClickedOutside")
      ) {
        binding.value(event);
      }
    };
    document.addEventListener("touchstart", el.eventClearDrag);
    document.addEventListener("touchmove", el.eventSetDrag);
    document.addEventListener("click", el.eventOnClick);
    document.addEventListener("touchend", el.eventOnClick);
  },
  unmounted: (el) => {
    document.removeEventListener("touchstart", el.eventClearDrag);
    document.removeEventListener("touchmove", el.eventSetDrag);
    document.removeEventListener("click", el.eventOnClick);
    document.removeEventListener("touchend", el.eventOnClick);
    el.removeAttribute("data-dragging");
  },
};

document.documentElement.lang = initialLocale;
setDayjsLocale(initialLocale);

const app = createApp(App);

app.directive("click-outside", clickOutside);
app.directive("tooltip", Tooltip);

app.component("Toaster", Toaster);
app.component("VueDatePicker", VueDatePicker);
app.component("v-select", VueSelect);
app.component("tabs", Tabs);
app.component("tab", Tab);

// ---- plugins
app.use(router);
app.use(store);
app.use(PrimeVue, {
  theme: {
    preset: {
      ...Aura,
      semantic: {
        ...Aura.semantic,
        primary: {
          50: "#ECFDF5",
          100: "#BBDEFB",
          200: "#90CAF9",
          300: "#64B5F6",
          400: "#42A5F5",
          500: "#2196F3",
          600: "#1E88E5",
          700: "#1976D2",
          800: "#1565C0",
          900: "#0D47A1",
          950: "#022c22",
        },
        surface: {
          50: "#F0F6F8",
          100: "#D5E5EA",
          200: "#cee0e7",
          300: "#c7dce3",
          400: "#c0d7e0",
          500: "#b9d3dc",
          600: "#A7BEC6",
          700: "#94a9b0",
          800: "#82949a",
          900: "#6f7f84",
          950: "#6f7f84",
        },
      },
    },
    options: {
      prefix: "p",
      darkModeSelector: "custom",
      cssLayer: false,
    },
  },
});
app.use(ConfirmationService);
app.use(ToastService);
app.use(VCalendar, {
  locales: {
    da: { firstDayOfWeek: 2 },
    en: { firstDayOfWeek: 1 },
  },
});
app.use(i18n);

// Keep Day.js + <html lang> in sync with vue-i18n
const loc = i18n.global.locale;
if (typeof loc !== "string") {
  // composition mode: it's a Ref
  watch(loc, (val) => {
    setDayjsLocale(val);
    document.documentElement.lang = val;
    localStorage.setItem("locale", val); // persist changes centrally
  });
}

app.mount("#app");
